import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { getAdminToken, setAdminSession, type AdminUser } from "../lib/auth";
import "./login.css";

type AuthStatus = {
  setupConfigured: boolean;
  bootstrapEnabled: boolean;
  accessSso: boolean;
};

type Mode = "login" | "setup";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || "/";

  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<AuthStatus>("/api/v1/auth/status", { admin: false })
      .then(setStatus)
      .catch(() => setStatus({ setupConfigured: false, bootstrapEnabled: false, accessSso: false }));
  }, []);

  if (getAdminToken()) {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "setup") {
        const res = await api<{ token: string; user: AdminUser }>("/api/v1/auth/setup", {
          method: "POST",
          admin: false,
          body: JSON.stringify({ email, password, setupCode }),
        });
        setAdminSession(res.token, res.user);
      } else {
        const res = await api<{ token: string; user: AdminUser }>("/api/v1/auth/login", {
          method: "POST",
          admin: false,
          body: JSON.stringify({ email, password }),
        });
        setAdminSession(res.token, res.user);
      }
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Sign-in failed";
      if (msg.includes("setup_required") || msg.toLowerCase().includes("no password")) {
        setMode("setup");
        setError("Create a password first using your one-time setup code.");
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <aside className="login-brand" aria-label="Spielmann Consulting">
        <div className="login-brand__top">
          <img className="login-brand__logo" src="/brand/spielmann-logo.svg" alt="Spielmann Consulting" />
        </div>
        <div className="login-brand__copy">
          <p className="login-brand__kicker">Secure data room</p>
          <h2 className="login-brand__title">Share deal documents with confidence</h2>
          <p className="login-brand__lead">
            Track every view. Control every download. Built for counsel, principals, and advisors.
          </p>
        </div>
        <p className="login-brand__meta">Restricted to authorised Spielmann administrators</p>
      </aside>

      <main className="login-side">
        <div className="login-panel">
          <h1>{mode === "setup" ? "Create your password" : "Sign in"}</h1>
          <p className="login-lead">
            {mode === "setup"
              ? "Use your allowlisted email and the one-time setup code from your administrator."
              : "Access Spaces, uploads, and sharing for Spielmann deal rooms."}
          </p>

          <form className="login-form" onSubmit={onSubmit}>
            <label>
              Email
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                autoComplete={mode === "setup" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={mode === "setup" ? 12 : 1}
                required
                placeholder={mode === "setup" ? "At least 12 characters" : undefined}
              />
            </label>
            {mode === "setup" && (
              <label>
                One-time setup code
                <input
                  type="password"
                  autoComplete="one-time-code"
                  value={setupCode}
                  onChange={(e) => setSetupCode(e.target.value)}
                  required
                />
              </label>
            )}
            {error && (
              <p className="login-error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" disabled={busy}>
              {busy ? "Signing in…" : mode === "setup" ? "Create password & continue" : "Sign in"}
            </button>
          </form>

          <div className="login-switch">
            {mode === "login" ? (
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setMode("setup");
                  setError(null);
                }}
                disabled={!status?.setupConfigured}
                title={status?.setupConfigured ? undefined : "Setup is not configured on the server"}
              >
                First-time setup
              </button>
            ) : (
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setMode("login");
                  setError(null);
                }}
              >
                Back to sign in
              </button>
            )}
          </div>
          {status?.accessSso ? (
            <p className="login-hint" style={{ marginTop: 16 }}>
              Cloudflare Access SSO is enabled for this deployment. Open the protected app URL to sign in with SSO.
            </p>
          ) : null}
          <p className="login-hint">Sessions expire after 12 hours.</p>
        </div>
      </main>
    </div>
  );
}
